import { useTranslations } from '@vocab/react';
import {
  Badge,
  Text,
  Stack,
  Inline,
  Columns,
  Column,
} from 'braid-design-system';
import type { ComponentPropsWithoutRef } from 'react';

import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CardError as CreateResumeCardError } from 'src/components/Profile/CreateResume/CardError/CardError';
import { isCreateResumeOptionalFields } from 'src/components/Profile/CreateResume/Validation/utils';
import type { CreateResumeRoute } from 'src/components/Profile/CreateResume/types';
import { CREATE_RESUME_PAGE } from 'src/components/Router';
import { usePage } from 'src/components/Router/usePage';
import { useRetryAction } from 'src/modules/retry-actions/hooks';

import { CardError } from '../CardError/CardError';

import translations from './.vocab';
import { CardTitle } from './CardTitle/CardTitle';

interface SubTitleProps {
  subTitle: React.ReactNode;
  loading?: boolean;
}

const CardSubTitle = ({ subTitle, loading }: SubTitleProps) =>
  loading ? (
    <LoadingItem type="text" size="standard" width="large" />
  ) : (
    <Text component="h2" tone="secondary">
      {subTitle}
    </Text>
  );

type TitleProps = ComponentPropsWithoutRef<typeof CardTitle>;

interface ComponentProps extends TitleProps {
  subTitle?: SubTitleProps['subTitle'];
  topAction?: React.ReactNode;
  showNewBadge?: boolean;
  showOptionalBadge?: boolean;
  retryAction?: () => void;
  loading?: boolean;
}

export const CardHeading = ({
  name,
  title,
  subTitle,
  topAction,
  showNewBadge,
  showOptionalBadge,
  loading,
}: ComponentProps) => {
  const { t } = useTranslations(translations);
  const page = usePage();
  const isCreateResumePage = page === CREATE_RESUME_PAGE;
  const hasBadge = showNewBadge || showOptionalBadge;
  const retryAction = useRetryAction(name);
  const showErrorAlert = Boolean(retryAction?.canRetry);

  return (
    <>
      <Stack space="xsmall">
        {hasBadge && (
          <Inline space="xsmall">
            {showNewBadge && <Badge tone="positive">{t('New')}</Badge>}
            {showOptionalBadge && <Badge tone="info">{t('Optional')}</Badge>}
          </Inline>
        )}
        <Stack space="small">
          <Columns space="small" alignY="center">
            <Column>
              <Stack space="small">
                <CardTitle title={title} name={name} loading={loading} />
                {subTitle ? (
                  <CardSubTitle subTitle={subTitle} loading={loading} />
                ) : null}
              </Stack>
            </Column>
            {topAction ? (
              <Column width="content">
                <Text>{topAction}</Text>
              </Column>
            ) : null}
          </Columns>
          {showErrorAlert ? <CardError card={name} /> : null}
        </Stack>
      </Stack>
      {isCreateResumePage &&
        !isCreateResumeOptionalFields(name as CreateResumeRoute) && (
          <CreateResumeCardError name={name as CreateResumeRoute} />
        )}
    </>
  );
};
